export interface CropBox { x: number; y: number; width: number; height: number }
export type CropAspect = number | null;

export const CROP_STEP = 0.02;
export const MIN_CROP_PIXELS = 64;

function limits(imageWidth: number, imageHeight: number) {
  return { width: Math.min(1, MIN_CROP_PIXELS / imageWidth), height: Math.min(1, MIN_CROP_PIXELS / imageHeight) };
}

/** Keeps a fractional crop inside its original image. */
export function clampCrop(box: CropBox, imageWidth: number, imageHeight: number): CropBox {
  const minimum = limits(imageWidth, imageHeight);
  const width = Math.max(minimum.width, Math.min(1, box.width));
  const height = Math.max(minimum.height, Math.min(1, box.height));
  return { x: Math.max(0, Math.min(1 - width, box.x)), y: Math.max(0, Math.min(1 - height, box.y)), width, height };
}

/** A pixel aspect becomes this aspect in the image's fractional coordinate system. */
export function fractionalAspect(pixelAspect: number, imageWidth: number, imageHeight: number): number {
  return pixelAspect * imageHeight / imageWidth;
}

/** The largest centred crop for a pixel aspect. */
export function aspectCrop(pixelAspect: number, imageWidth: number, imageHeight: number): CropBox {
  const aspect = fractionalAspect(pixelAspect, imageWidth, imageHeight);
  const width = Math.min(1, aspect);
  const height = Math.min(1, 1 / aspect);
  return { x: (1 - width) / 2, y: (1 - height) / 2, width, height };
}

export function moveCrop(box: CropBox, dx: number, dy: number, imageWidth: number, imageHeight: number): CropBox {
  return clampCrop({ ...box, x: box.x + dx, y: box.y + dy }, imageWidth, imageHeight);
}

/** Resizes from one of the four corners, preserving a locked pixel aspect. */
export function resizeCrop(box: CropBox, corner: "nw" | "ne" | "sw" | "se", dx: number, dy: number, imageWidth: number, imageHeight: number, aspect: CropAspect): CropBox {
  const left = box.x; const top = box.y; const right = box.x + box.width; const bottom = box.y + box.height;
  const movingLeft = corner === "nw" || corner === "sw"; const movingTop = corner === "nw" || corner === "ne";
  let nextLeft = movingLeft ? left + dx : left; let nextRight = movingLeft ? right : right + dx;
  let nextTop = movingTop ? top + dy : top; let nextBottom = movingTop ? bottom : bottom + dy;
  if (aspect) {
    const fractionAspect = fractionalAspect(aspect, imageWidth, imageHeight);
    const requested = Math.max(Math.abs(nextRight - nextLeft), Math.abs(nextBottom - nextTop) * fractionAspect);
    const maximum = Math.min(movingLeft ? right : 1 - left, (movingTop ? bottom : 1 - top) * fractionAspect);
    const width = Math.min(requested, maximum);
    const height = width / fractionAspect;
    if (movingLeft) nextLeft = right - width; else nextRight = left + width;
    if (movingTop) nextTop = bottom - height; else nextBottom = top + height;
  }
  const minimum = limits(imageWidth, imageHeight);
  const width = Math.max(minimum.width, nextRight - nextLeft); const height = Math.max(minimum.height, nextBottom - nextTop);
  if (movingLeft) nextLeft = right - width; else nextRight = left + width;
  if (movingTop) nextTop = bottom - height; else nextBottom = top + height;
  return clampCrop({ x: nextLeft, y: nextTop, width: nextRight - nextLeft, height: nextBottom - nextTop }, imageWidth, imageHeight);
}

export function keyboardCrop(box: CropBox, key: "ArrowLeft" | "ArrowRight" | "ArrowUp" | "ArrowDown", resize: boolean, imageWidth: number, imageHeight: number, aspect: CropAspect, step = CROP_STEP): CropBox {
  const delta = key === "ArrowLeft" || key === "ArrowUp" ? -step : step;
  if (!resize) return moveCrop(box, key === "ArrowLeft" || key === "ArrowRight" ? delta : 0, key === "ArrowUp" || key === "ArrowDown" ? delta : 0, imageWidth, imageHeight);
  return resizeCrop(box, key === "ArrowLeft" || key === "ArrowUp" ? "nw" : "se", key === "ArrowLeft" || key === "ArrowRight" ? delta : 0, key === "ArrowUp" || key === "ArrowDown" ? delta : 0, imageWidth, imageHeight, aspect);
}

/** The exact integer source rectangle used by canvas.drawImage. */
export function cropToPixels(box: CropBox, imageWidth: number, imageHeight: number): CropBox {
  const safe = clampCrop(box, imageWidth, imageHeight);
  const x = Math.max(0, Math.min(imageWidth - 1, Math.round(safe.x * imageWidth)));
  const y = Math.max(0, Math.min(imageHeight - 1, Math.round(safe.y * imageHeight)));
  return { x, y, width: Math.max(1, Math.min(imageWidth - x, Math.round(safe.width * imageWidth))), height: Math.max(1, Math.min(imageHeight - y, Math.round(safe.height * imageHeight))) };
}

export function cropOutputSize(box: CropBox, imageWidth: number, imageHeight: number, maxEdge = 2000): { width: number; height: number } {
  const pixels = cropToPixels(box, imageWidth, imageHeight);
  const scale = Math.min(1, maxEdge / Math.max(pixels.width, pixels.height));
  return { width: Math.max(1, Math.round(pixels.width * scale)), height: Math.max(1, Math.round(pixels.height * scale)) };
}
